/**
 * Property configuration on the client.
 *
 * The mirror of `apps/server/src/config/property.ts`: one place that answers "where
 * is this property and what time is it there", so no component reaches for
 * `Intl.DateTimeFormat().resolvedOptions().timeZone` on its own.
 */

/**
 * Refuses a missing value, the way the server's `required()` does.
 *
 * This used to fall back to the device timezone, which is worse than a wrong constant.
 * A wrong constant is one wrong answer that somebody eventually notices; the device
 * zone is a *different* wrong answer on every handset, each one entirely plausible, and
 * no error anywhere. Vite does not fail a build over a missing `VITE_` variable either
 * — it inlines `undefined`, and the `||` took the fallback in silence.
 *
 * An empty string counts as missing, matching the server: `docker compose` passes
 * `VITE_PROPERTY_TIMEZONE: ${PROPERTY_TIMEZONE}` with no default, so an unset
 * `PROPERTY_TIMEZONE` arrives as an empty build argument rather than as an absent one.
 */
function required(name: string, value: string | undefined): string {
  if (value && value.trim() !== '') return value.trim();

  throw new Error(
    `Missing required build-time variable: ${name}. See .env.example. Vite compiles ` +
      'it into the bundle, so set it and REBUILD the client — a restart re-runs the ' +
      'old bundle. There is deliberately no device-timezone fallback: it gave a ' +
      'different wrong answer on every handset, with no error anywhere.',
  );
}

/**
 * The property's IANA timezone.
 *
 * Must equal the server's `PROPERTY_TIMEZONE`; `docker-compose.yml` feeds this from
 * that one so the two cannot drift in a Docker build, and `property.env.test.ts`
 * asserts they agree in the templates.
 */
export const propertyTimeZone: string = required(
  'VITE_PROPERTY_TIMEZONE',
  import.meta.env.VITE_PROPERTY_TIMEZONE,
);
